import { useCallback, useEffect, useRef } from "react";

import AuthCaptcha from "./AuthCaptcha";
import {
  buildNativeAuthCaptchaResultUrl,
  isExactNativeAuthCaptchaLocation,
} from "~/utils/nativeAuthCaptcha";

const URL_SAFE_NONCE = /^[A-Za-z0-9_-]{64}$/;
const INTERACTION_FALLBACK_DELAY_MS = 5000;

const NativeAuthCaptcha = ({ nonce, trustedOrigin, onNavigate }) => {
  const captchaRef = useRef(null);
  const proofPostedRef = useRef(false);
  const readyPostedRef = useRef(false);
  const interactionPostedRef = useRef(false);
  const interactionTimerRef = useRef(null);

  const clearInteractionTimer = useCallback(() => {
    if (interactionTimerRef.current) {
      window.clearTimeout(interactionTimerRef.current);
      interactionTimerRef.current = null;
    }
  }, []);

  const postBridgeStatus = useCallback(
    (type) => {
      const bridge = window.ReactNativeWebView;
      if (!bridge || typeof bridge.postMessage !== "function") {
        return false;
      }
      bridge.postMessage(JSON.stringify({ type, nonce }));
      return true;
    },
    [nonce]
  );

  const handleStatusChange = useCallback(
    (status) => {
      if (status !== "ready" || readyPostedRef.current) {
        if (
          status === "verifying" &&
          !interactionPostedRef.current &&
          !interactionTimerRef.current
        ) {
          interactionTimerRef.current = window.setTimeout(() => {
            interactionTimerRef.current = null;
            if (proofPostedRef.current || interactionPostedRef.current) {
              return;
            }
            interactionPostedRef.current = true;
            postBridgeStatus("interaction_required");
          }, INTERACTION_FALLBACK_DELAY_MS);
        }
        return;
      }
      readyPostedRef.current = true;
      postBridgeStatus("ready");
    },
    [postBridgeStatus]
  );

  const handleCaptchaError = useCallback(() => {
    clearInteractionTimer();
    postBridgeStatus("failed");
  }, [clearInteractionTimer, postBridgeStatus]);

  useEffect(() => clearInteractionTimer, [clearInteractionTimer]);

  const startPreloadedCaptcha = useCallback(
    (requestNonce) => {
      if (requestNonce !== nonce || !URL_SAFE_NONCE.test(requestNonce || "")) {
        return;
      }
      captchaRef.current?.start();
    },
    [nonce]
  );

  useEffect(() => {
    const previousStart = window.__eleduckStartNativeAuthCaptcha;
    window.__eleduckStartNativeAuthCaptcha = startPreloadedCaptcha;

    return () => {
      if (window.__eleduckStartNativeAuthCaptcha === startPreloadedCaptcha) {
        if (previousStart) {
          window.__eleduckStartNativeAuthCaptcha = previousStart;
        } else {
          delete window.__eleduckStartNativeAuthCaptcha;
        }
      }
    };
  }, [startPreloadedCaptcha]);

  const postVerifiedProof = useCallback(
    async (captchaVerifyParam) => {
      if (!URL_SAFE_NONCE.test(nonce || "")) {
        throw new Error("原生验证请求无效，请返回后重试");
      }

      if (
        typeof captchaVerifyParam !== "string" ||
        captchaVerifyParam.trim().length === 0
      ) {
        throw new Error("人机验证失败，请重试");
      }

      if (
        typeof window === "undefined" ||
        !isExactNativeAuthCaptchaLocation(
          window.location.href,
          nonce,
          trustedOrigin
        )
      ) {
        throw new Error("原生验证请求已失效，请返回后重试");
      }

      if (proofPostedRef.current) {
        return;
      }

      clearInteractionTimer();

      const bridge = window.ReactNativeWebView;
      if (!bridge || typeof bridge.postMessage !== "function") {
        throw new Error("请在应用内完成验证");
      }

      bridge.postMessage(
        JSON.stringify({
          type: "auth_captcha_verified",
          captcha_verify_param: captchaVerifyParam,
          nonce,
        })
      );
      proofPostedRef.current = true;
      // Android's ReactNativeWebView JavaScript interface is exposed to child
      // frames, so the native app also receives a top-frame-only callback URL.
      (onNavigate || ((url) => window.location.assign(url)))(
        buildNativeAuthCaptchaResultUrl(nonce, captchaVerifyParam)
      );
    },
    [clearInteractionTimer, nonce, onNavigate, trustedOrigin]
  );

  if (!URL_SAFE_NONCE.test(nonce || "") || !trustedOrigin) {
    return null;
  }

  return (
    <AuthCaptcha
      ref={captchaRef}
      visibleTrigger
      autoStart={false}
      onError={handleCaptchaError}
      onStatusChange={handleStatusChange}
      onVerified={postVerifiedProof}
    />
  );
};

export default NativeAuthCaptcha;
